import { DataResult, State, toODataString } from '@progress/kendo-data-query';
import { AxiosRequestConfig } from 'axios';
import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { HttpResponse } from '../ajax';
import LoadingSpinner from './LoadingSpinner';

interface DataLoaderProps {
  dataState: State;
  onDataReceived: (data: DataResult, showErrorPanel?: boolean) => void;
  getData: (
    queryString?: string,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<any>>;
  optionalQueryString?: string;
  bypassRequestDataIfNeeded?: boolean;
  reloadTrigger?: string;
}

export const DataLoader = (props: DataLoaderProps) => {
  const {
    dataState,
    onDataReceived,
    getData,
    optionalQueryString,
    bypassRequestDataIfNeeded,
    reloadTrigger,
  } = props;
  const lastSuccess = useRef<string>('');
  const lastReloadTrigger = useRef<string | undefined>(undefined);
  const pending = useRef<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const requestDataIfNeeded = useCallback(
    async (abortController?: AbortController) => {
      const getFullQueryString = () => {
        return `${toODataString(dataState)}&$count=true${
          !_.isNil(optionalQueryString) ? `&${optionalQueryString}` : ''
        }`;
      };

      const hasReloadTriggered =
        reloadTrigger !== undefined &&
        reloadTrigger !== lastReloadTrigger.current;

      if (
        !bypassRequestDataIfNeeded &&
        !hasReloadTriggered &&
        (pending.current || getFullQueryString() === lastSuccess.current)
      ) {
        return;
      }
      pending.current = getFullQueryString();

      if (reloadTrigger !== undefined) {
        lastReloadTrigger.current = reloadTrigger;
      }

      setLoading(true);
      const response = await getData(`${pending.current}`, {
        signal: abortController?.signal,
      });

      if (!response.hasErrors) {
        lastSuccess.current = pending.current;
        pending.current = '';

        if (getFullQueryString() === lastSuccess.current) {
          if (Array.isArray(response.data)) {
            onDataReceived(
              {
                data: response.data,
                total: response.data.length,
              },
              false,
            );
          } else {
            onDataReceived(
              {
                data: response.data.value,
                total: response.data['@odata.count'],
              },
              false,
            );
          }
        } else {
          requestDataIfNeeded(abortController);
        }
      } else {
        onDataReceived(
          {
            data: [],
            total: 0,
          },
          true,
        );
      }
      setLoading(false);
    },
    [
      getData,
      dataState,
      optionalQueryString,
      onDataReceived,
      bypassRequestDataIfNeeded,
      reloadTrigger,
    ],
  );

  useEffect(() => {
    requestDataIfNeeded();
  }, [requestDataIfNeeded, reloadTrigger]);

  return loading ? <LoadingPanel /> : null;
};

const LoadingPanel = () => {
  const loadingPanel = (
    <div className="k-loading-mask">
      <LoadingSpinner />
    </div>
  );

  const gridContent = document?.querySelector('.k-grid-content');

  return gridContent ? ReactDOM.createPortal(loadingPanel, gridContent) : null;
};
